import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import type { ActivationSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, hasText } from "../format";

/**
 * Patriot's `pendingState` and `completedStatus`, verbatim.
 *
 * Neither is derivable from the other, so an open activation shows both. Once
 * it is closed, though, Patriot leaves the last `pendingState` behind (e.g.
 * `ON_HOLD` beside `COMPLETED`), which reads as an open problem — so a closed
 * activation shows only its completed status. The detail view still lists both
 * fields verbatim.
 */
export function ActivationStatus({
  activation,
}: {
  activation: Pick<ActivationSummary, "pendingState" | "completedStatus">;
}) {
  const { completedStatus } = activation;
  const pendingState = isClosed(completedStatus) ? undefined : activation.pendingState;

  if (!hasText(pendingState) && !hasText(completedStatus)) {
    return (
      <Text textStyle="xs/regular" color="text">
        {EMPTY}
      </Text>
    );
  }

  return (
    <View flexDirection="row" flexWrap="wrap" gap="xs">
      {hasText(pendingState) && (
        <View style={[styles.chip, styles.pending]} accessibilityLabel={`Pending state ${pendingState}`}>
          <Text textStyle="xxs/semibold" style={styles.pendingText}>
            {pendingState}
          </Text>
        </View>
      )}
      {hasText(completedStatus) && (
        <View style={[styles.chip, styles.completed]} accessibilityLabel={`Completed status ${completedStatus}`}>
          <Text textStyle="xxs/semibold" style={styles.completedText}>
            {completedStatus}
          </Text>
        </View>
      )}
    </View>
  );
}

/**
 * Patriot's `AlarmCompleteStatus` also has `PENDING` and `SLEEPING`, which are
 * still-open activations, so only these two close one.
 */
const CLOSED_STATUSES = new Set(["COMPLETED", "CANCELLED"]);

function isClosed(completedStatus: string | undefined): boolean {
  return completedStatus !== undefined && CLOSED_STATUSES.has(completedStatus);
}

const styles = StyleSheet.create((theme) => ({
  chip: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
  },
  pending: { borderColor: theme.colors.status.warning },
  completed: { borderColor: theme.colors.borderCard },
  pendingText: { color: theme.colors.status.warning },
  completedText: { color: theme.colors.textMuted },
}));
